import React from 'react';
import { QueryStringFeatures } from './QueryStringFeatures';
import { setup, RoutePath, SetupOverrides, useTranslation } from '@shared/core';
import { Setup } from './Setup';
import { Route, Switch, Redirect } from '@react-router';
import { ErrorBoundary } from './ErrorBoundary';
import { ssrRoutes } from './router.ssr';
import { Helmet } from 'react-helmet-async';
import { useFreezeNavigation } from '@shared/utils/hooks/useFreezeNavigation';
import { EventRedirect } from '@shared/components/EventRedirect';
export type AppProps = Readonly<
  Partial<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    routes: Array<RoutePath<any>>;
    helmetContext: Object;
    setupOverrides?: SetupOverrides;
    defaultScreen?: 'mobile' | 'tablet' | 'desktop';
  }>
>;

const Routes = ({ routes = ssrRoutes }: Pick<AppProps, 'routes'>) => {
  const { location } = useFreezeNavigation();
  return (
    <Switch location={location}>
      <Route
        exact={true}
        path="/eventredirect/:eventId"
        render={({ match, staticContext, location }) => {
          return match && <EventRedirect eventId={match.params.eventId} location={location} staticContext={staticContext} />;
        }}
      />
      <Redirect from="/me" to="/account/events" />
      {routes.map(route => (
        <Route key={route.path} path={route.path} render={route.component} />
      ))}
    </Switch>
  );
};

const AppRoot: React.FC = ({ children }) => {
  const { t2 } = useTranslation('ssrServer');
  const translations = t2('metatags');
  return (
    <>
      <QueryStringFeatures />
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta property="og:title" content={translations.title} />
        <meta property="og:site_name" content="Joy" />
        <meta property="og:url" content="https://withjoy.com" />
      </Helmet>
      {children}
    </>
  );
};

const App: React.FC<AppProps> = ({ defaultScreen, routes, helmetContext = {}, setupOverrides }) => {
  const setupConfig = setup(setupOverrides);
  return (
    <>
      <ErrorBoundary>
        <Setup {...setupConfig} defaultScreen={defaultScreen} helmetContext={helmetContext}>
          <AppRoot>
            <Routes routes={routes} />
          </AppRoot>
        </Setup>
      </ErrorBoundary>
    </>
  );
};

export default App;
